import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from "primereact/button";

import UploadInput from '../../../components/UploadInput';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "../../../hooks/ApiHook";
import useToast from "../../../hooks/ToastHook";

const IndustryUpdateForm = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { showSuccess, showAxiosError } = useToast();

  const [logoFile, setLogoFile] = useState(null)
  const [logoUrl, setLogoUrl] = useState('')
  const [certificateFile, setCertificateFile] = useState(null)
  const [certificateUrl, setCertificateUrl] = useState('')
  const [certificatePassword, setCertificatePassword] = useState('')
  const [seriesNumber, setSeriesNumber] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState(0)
  const [isSimplesNacional, setIsSimplesNacional] = useState(false)
  const [simplesNacionalAliquot, setSimplesNacionalAliquot] = useState('')

  const [saving, setSaving] = useState(false)

  const isSaveDisabled = () => {
    if(!seriesNumber && !invoiceNumber && !certificatePassword) return true;
    if(isSimplesNacional && !simplesNacionalAliquot) return true;
    if(!logoFile && !logoUrl) return true;
    if(!certificateFile && !certificateUrl) return true;
    
    return false;
  }

  const uploadLogoFile = async () => {
    if(!logoFile) return;

    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      const response = await api.put('/settings/logo', formData);
      return response.data.url;
    } catch (error) {
      throw error;
    }
  }

  const uploadCertificateFile = async () => {
    if(!certificateFile) return;

    try {
      const formData = new FormData();
      formData.append('file', certificateFile);
      formData.append('password', certificatePassword);

      const response = await api.put('/settings/certificate', formData);
      return response.data.url;
    } catch (error) {
      throw error;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        nfe: {
          seriesNumber,
          sequence: Number(invoiceNumber),
        },
        sn: {
          active: isSimplesNacional,
          taxRate: simplesNacionalAliquot
        }
      }

      await api.put('/settings', data);
      await uploadLogoFile();
      await uploadCertificateFile();

      showSuccess('Salvo com sucesso', 'As configurações da empresa foram salvas com sucesso.');
      navigate(-1);
    } catch (error) {
      showAxiosError(error)
    } finally {
      setSaving(false);
    }    
  }

  const getSettings = async () => {
    setSaving(true);

    try {
      const { data } = await api.get('/settings');
      const { nfe, sn } = data;

      setSeriesNumber(nfe.seriesNumber);
      setInvoiceNumber(nfe.sequence);
      setLogoUrl(nfe.logoFileUrl);
      setCertificatePassword(nfe.certificatePassword);
      setCertificateUrl(nfe.certificateUrl || 'certificado.pfx');
      setIsSimplesNacional(sn.active);
      setSimplesNacionalAliquot(sn.taxRate);
    } catch (error) {
      showAxiosError(error);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    getSettings()
  }, [])

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-y-4">
        <div className="field col-12">
          <UploadInput 
            label="Logotipo"
            buttonText="Selecionar arquivo"
            name="logo"
            accept="image/*"
            helperText="O arquivo deve ser jpg, jpeg ou png."
            disabled={saving}
            setFile={setLogoFile}
            fileUrl={logoUrl}
          />
        </div>

        <div className="field col-12">
          <UploadInput 
            label="Certificado digital"
            buttonText="Selecionar arquivo"
            name="certificate"
            accept=".pfx"
            helperText="O arquivo deve ser pfx."
            disabled={saving}
            setFile={setCertificateFile}
            fileUrl={certificateUrl}
          />
        </div>

        <div className="field col-12">
          <label htmlFor="certificatePassword" className="text-gray-500 text-base">Senha do certificado digital</label>
          <InputText
            id="certificatePassword"
            value={certificatePassword}
            maxLength={50}
            onChange={e => setCertificatePassword(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="seriesNumber" className="text-gray-500 text-base">Numero da série</label>
          <InputText
            id="seriesNumber"
            value={seriesNumber}
            maxLength={50}
            onChange={e => setSeriesNumber(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="invoiceNumber" className="text-gray-500 text-base">Sequência da nota</label>
          <InputNumber
            id="invoiceNumber"
            value={invoiceNumber}
            useGrouping={false}
            min={0}
            onChange={e => setInvoiceNumber(e.value)}
            className="w-full"
          />
        </div>

        <div className="field col-12 flex align-items-center gap-2">
          <InputSwitch checked={isSimplesNacional} onChange={(e) => setIsSimplesNacional(e.value)} />
          <span className="text-gray-600 text-sm">Possui Simples Nacional</span>
        </div>

        {isSimplesNacional && (
          <div className="field col-12 md:col-6">
            <label htmlFor="aliquot" className="text-gray-500 text-base">Alíquota do Simples Nacional</label>
            <InputNumber
              id="aliquot"
              value={simplesNacionalAliquot}
              useGrouping={false}
              min={0}
              onChange={e => setSimplesNacionalAliquot(e.value)}
              className="w-full"
            />
          </div>
        )}

        <div className="mt-2 col-12">
          <Button 
            className="mr-2" 
            label="Salvar"
            loading={saving}
            disabled={isSaveDisabled()}
          />
          <Button 
            label="Cancelar" 
            outlined
            type="button"
            disabled={saving}
            severity="secondary"
            onClick={() => navigate(-1)}
          />
        </div>
      </div>
    </form>
  )
}

export default IndustryUpdateForm;